/**
 * fusionBridge.ts — Typed wrapper around the Fusion 360 palette
 * communication API (`window.adsk.fusionSendData`).
 *
 * Protocol (matches bridge/palette.py in GienoxFusion.bundle):
 *
 *   Web → Python:
 *     { type: "tool_call", id: "<uuid>", tool: "<name>", params: { ... } }
 *     { type: "ping" }
 *
 *   Python → Web:
 *     { type: "tool_result", id: "<uuid>", result: <any>, error: null }
 *     { type: "fusion_event", event: "<name>", data: { ... } }
 *     { type: "pong" }
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ToolCallRequest {
  type: 'tool_call';
  id: string;
  tool: string;
  params: Record<string, unknown>;
}

export interface ToolCallResult {
  type: 'tool_result';
  id: string;
  result: unknown;
  error: string | null;
}

export interface FusionEvent {
  type: 'fusion_event';
  event: string;
  data: Record<string, unknown>;
}

type IncomingMessage = ToolCallResult | FusionEvent | { type: 'pong' };

type FusionEventCallback = (event: string, data: Record<string, unknown>) => void;

// ---------------------------------------------------------------------------
// Fusion palette API typing (injected by Fusion 360 Qt WebEngine)
// ---------------------------------------------------------------------------

declare global {
  interface Window {
    adsk?: {
      fusionSendData: (action: string, data: string) => void | Promise<void>;
    };
  }
}

// ---------------------------------------------------------------------------
// Bridge class
// ---------------------------------------------------------------------------

const TOOL_CALL_TIMEOUT_MS = 30_000; // 30 seconds max for a tool call

class FusionBridge {
  private _pendingCalls = new Map<string, {
    resolve: (value: unknown) => void;
    reject: (reason: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }>();

  private _eventListeners: FusionEventCallback[] = [];
  private _connected = false;
  private _initialized = false;

  // ── Detection ──────────────────────────────────────────────────────────

  /** Returns true if running inside a Fusion 360 palette. */
  isInsideFusion(): boolean {
    if (typeof window === 'undefined') return false;
    // Direct check
    if (window.adsk?.fusionSendData) return true;
    // Flag set after async polling detects window.adsk
    if ((window as any).__fusionBridgeReady) return true;
    return false;
  }

  /** Initialize the bridge: listen for incoming messages from Python. */
  init(): void {
    if (this._initialized) return;
    this._initialized = true;

    // ── Handler for messages sent from Python via palette.sendInfoToHTML() ──
    // Qt WebEngine palette calls window.fusionJavaScriptHandler.handle(action, data)
    (window as any).fusionJavaScriptHandler = {
      handle: (action: string, data: string) => {
        this._handleMessage(action, data);
        return '';
      },
    };

    // Legacy / CEF handler
    (window as any).__fusionBridgeReceive = (action: string, data: string) => {
      this._handleMessage(action, data);
    };

    // CustomEvent fallback
    document.addEventListener('fusionMessage', ((e: CustomEvent) => {
      if (e.detail) {
        this._handleMessage('message', typeof e.detail === 'string' ? e.detail : JSON.stringify(e.detail));
      }
    }) as EventListener);

    // ── Poll for window.adsk (Qt WebEngine injects it asynchronously) ────
    // The palette redirects to 3d.gienox.com, and Fusion injects window.adsk
    // after the page loads. We poll for up to 5 seconds.
    const pollStart = Date.now();
    const poll = setInterval(() => {
      if (window.adsk?.fusionSendData) {
        clearInterval(poll);
        (window as any).__fusionBridgeReady = true;
        console.log('[FusionBridge] window.adsk detected, pinging Python bridge...');
        // Notify React to re-ping and update bridgeConnected state
        window.dispatchEvent(new CustomEvent('fusion_adsk_ready'));
        this.ping().then(ok => {
          console.log('[FusionBridge] ping result:', ok);
        });
      } else if (Date.now() - pollStart > 5000) {
        clearInterval(poll);
        console.log('[FusionBridge] window.adsk not found after 5s (not inside Fusion palette)');
      }
    }, 200);

    console.log('[FusionBridge] Initialized, polling for window.adsk...');
  }

  // ── Ping / Health ──────────────────────────────────────────────────────

  /** Ping the Python bridge and wait for a pong. Returns true if connected. */
  async ping(timeoutMs = 3000): Promise<boolean> {
    if (!this.isInsideFusion()) {
      this._connected = false;
      return false;
    }

    return new Promise<boolean>((resolve) => {
      const timer = setTimeout(() => {
        this._connected = false;
        resolve(false);
      }, timeoutMs);

      // Temporarily capture the pong
      const originalHandler = (window as any).__fusionBridgePongResolve;
      (window as any).__fusionBridgePongResolve = () => {
        clearTimeout(timer);
        this._connected = true;
        (window as any).__fusionBridgePongResolve = originalHandler;
        resolve(true);
      };

      this._send({ type: 'ping' });
    });
  }

  /** Whether the last ping succeeded. */
  get connected(): boolean {
    return this._connected;
  }

  // ── Tool Calls ─────────────────────────────────────────────────────────

  /**
   * Call a Fusion API tool and wait for the result.
   *
   * @param tool  Tool name (e.g. "get_model_summary", "extrude")
   * @param params Tool parameters
   * @returns Tool result (or throws on error/timeout)
   */
  async callTool(tool: string, params: Record<string, unknown> = {}): Promise<unknown> {
    if (!this.isInsideFusion()) {
      throw new Error('Not running inside Fusion 360 palette');
    }

    const id = crypto.randomUUID();

    return new Promise<unknown>((resolve, reject) => {
      const timer = setTimeout(() => {
        this._pendingCalls.delete(id);
        reject(new Error(`Tool call "${tool}" timed out after ${TOOL_CALL_TIMEOUT_MS}ms`));
      }, TOOL_CALL_TIMEOUT_MS);

      this._pendingCalls.set(id, { resolve, reject, timer });

      const message: ToolCallRequest = {
        type: 'tool_call',
        id,
        tool,
        params,
      };

      this._send(message as unknown as Record<string, unknown>);
    });
  }

  // ── Event Subscription ─────────────────────────────────────────────────

  /**
   * Subscribe to Fusion events (e.g. selection_changed).
   * Returns an unsubscribe function.
   */
  onFusionEvent(callback: FusionEventCallback): () => void {
    this._eventListeners.push(callback);
    return () => {
      this._eventListeners = this._eventListeners.filter((cb) => cb !== callback);
    };
  }

  // ── Internal ───────────────────────────────────────────────────────────

  /** Send a JSON message to the Python bridge. */
  private _send(payload: Record<string, unknown>): void {
    try {
      const data = JSON.stringify(payload);
      window.adsk?.fusionSendData('message', data);
    } catch (err) {
      console.error('[FusionBridge] Send failed:', err);
    }
  }

  /** Handle an incoming message from the Python bridge. */
  private _handleMessage(_action: string, rawData: string): void {
    let msg: IncomingMessage;
    try {
      msg = JSON.parse(rawData);
    } catch {
      console.warn('[FusionBridge] Invalid JSON from Fusion:', rawData);
      return;
    }

    switch (msg.type) {
      case 'tool_result': {
        const pending = this._pendingCalls.get(msg.id);
        if (pending) {
          clearTimeout(pending.timer);
          this._pendingCalls.delete(msg.id);

          if (msg.error) {
            pending.reject(new Error(msg.error));
          } else {
            pending.resolve(msg.result);
          }
        }
        break;
      }

      case 'fusion_event': {
        for (const cb of this._eventListeners) {
          try {
            cb(msg.event, msg.data);
          } catch (err) {
            console.error('[FusionBridge] Event listener error:', err);
          }
        }
        break;
      }

      case 'pong': {
        this._connected = true;
        const pongResolve = (window as any).__fusionBridgePongResolve;
        if (typeof pongResolve === 'function') {
          pongResolve();
        }
        break;
      }
    }
  }
}

// ---------------------------------------------------------------------------
// Singleton export
// ---------------------------------------------------------------------------

export const fusionBridge = new FusionBridge();
export default fusionBridge;
